import { useQuery } from '@tanstack/react-query'
import { Users, ShieldCheck } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { PageHeader } from '@/components/shared/page-header'
import { EmptyState } from '@/components/shared/empty-state'
import { supabase } from '@/lib/supabase/client'
import { cn } from '@/lib/utils/cn'

// ─── Types ───────────────────────────────────────────────────────────────────

interface AdminUser {
  id: string
  full_name: string
  is_super_user: boolean
  is_disabled: boolean
  created_at: string
}

// ─── Query ───────────────────────────────────────────────────────────────────

async function fetchAdminUsers(): Promise<AdminUser[]> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, full_name, is_super_user, is_disabled, created_at')
    .order('created_at', { ascending: true })

  if (error) throw error
  return data ?? []
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

function formatJoinDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('fa-IR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function PlatformUsersPage() {
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: fetchAdminUsers,
  })

  const totalSuperUsers = users.filter((u) => u.is_super_user).length
  const totalDisabled = users.filter((u) => u.is_disabled).length

  return (
    <div className="p-6">
      <PageHeader
        title="کاربران"
        subtitle="فهرست تمام کاربران ثبت‌شده در پلتفرم"
      />

      {/* Summary chips */}
      {!isLoading && users.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4 mb-6">
          <Badge className="bg-muted text-muted-foreground border border-border font-normal text-xs">
            {users.length.toLocaleString('fa-IR')} کاربر
          </Badge>
          {totalSuperUsers > 0 && (
            <Badge className="bg-primary/15 text-primary border border-primary/30 font-normal text-xs">
              {totalSuperUsers.toLocaleString('fa-IR')} سوپر یوزر
            </Badge>
          )}
          {totalDisabled > 0 && (
            <Badge className="bg-destructive/15 text-red-400 border border-destructive/30 font-normal text-xs">
              {totalDisabled.toLocaleString('fa-IR')} غیرفعال
            </Badge>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-card border border-border rounded-lg p-4 animate-pulse flex items-center gap-3">
              <div className="w-9 h-9 bg-muted rounded-full" />
              <div className="flex-1 space-y-1.5">
                <div className="h-4 bg-muted rounded w-40" />
                <div className="h-3 bg-muted rounded w-56" />
              </div>
              <div className="h-5 bg-muted rounded w-20" />
            </div>
          ))}
        </div>
      ) : users.length === 0 ? (
        <EmptyState
          icon={Users}
          title="کاربری یافت نشد"
          description="هنوز هیچ کاربری در سیستم ثبت نشده است"
        />
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-4 px-4 py-2.5 border-b border-border bg-muted/30">
            <div className="w-9" />
            <p className="text-xs font-medium text-muted-foreground">نام کاربر</p>
            <p className="text-xs font-medium text-muted-foreground text-center w-24">نقش</p>
            <p className="text-xs font-medium text-muted-foreground text-end w-32">تاریخ عضویت</p>
          </div>

          {/* Rows */}
          <div className="divide-y divide-border">
            {users.map((u) => (
              <div
                key={u.id}
                className={cn(
                  'grid grid-cols-[auto_1fr_auto_auto] items-center gap-4 px-4 py-3 transition-colors hover:bg-muted/20',
                  u.is_disabled && 'opacity-50'
                )}
              >
                {/* Avatar */}
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarFallback className="bg-primary/20 text-primary text-xs font-medium">
                    {getInitials(u.full_name)}
                  </AvatarFallback>
                </Avatar>

                {/* Name */}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{u.full_name}</p>
                  {u.is_disabled && (
                    <p className="text-xs text-muted-foreground">غیرفعال</p>
                  )}
                </div>

                {/* Role badge */}
                <div className="w-24 flex justify-center">
                  {u.is_super_user ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-primary/15 text-primary border border-primary/30">
                      <ShieldCheck className="w-3 h-3" />
                      سوپر یوزر
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-muted text-muted-foreground border border-border">
                      کاربر
                    </span>
                  )}
                </div>

                {/* Join date */}
                <p className="text-xs text-muted-foreground w-32 text-end">
                  {formatJoinDate(u.created_at)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
